// src/features/planning/pages/MrpRunDetailPage.tsx
// ADR-094 Rule MRP-FE-02/MRP-FE-03: renders the flat `requirements` array
// as an indented tree by parent_requirement, not a flat table.
//
// ⚠️ Hook name useV1PlanningMrpRunsRetrieve inferred — verify (ADR-094).

import { useParams } from "react-router-dom";
import { useV1PlanningMrpRunsRetrieve, useV1InventoryMaterialsList } from "@/api/generated/v1/v1";
import { SourcingBadge } from "../components/SourcingBadge";
import {
  buildRequirementTree,
  flattenRequirementTree,
} from "../utils/buildRequirementTree";
import { extractData, extractResults } from "@/shared/utils/apiResponseUtils";
import { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { PageBanner } from "@/shared/components/PageBanner";
import { useNavigate } from "react-router-dom";

export function MrpRunDetailPage() {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { data: runResp, isLoading: isRunLoading } = useV1PlanningMrpRunsRetrieve(runId!);
  const run = extractData<any>(runResp);

  const { data: materialsData, isLoading: isMaterialsLoading } = useV1InventoryMaterialsList();
  const materials = extractResults<any>(materialsData);
  const getMaterialName = (id: string) => materials.find((m: any) => m.id === id)?.code || id;

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    try {
      return new DateObject({ date: dateString, format: "YYYY-MM-DD" })
        .convert(persian, persian_fa)
        .format("YYYY/MM/DD");
    } catch {
      return dateString;
    }
  };

  if (isRunLoading || isMaterialsLoading) return <p>در حال بارگذاری...</p>;
  if (!run) return <p>اجرای MRP یافت نشد.</p>;

  const tree = buildRequirementTree(run.requirements);
  const rows = flattenRequirementTree(tree);

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title="نتیجهٔ اجرای MRP"
        subtitle={`تاریخ اجرا: ${new Date(run.executed_at).toLocaleString("fa-IR")} — ${run.requirement_count} نیاز شناسایی شد`}
        icon="⚙️"
        helpLink="/help/planning/mrp-run-detail"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          <button
            onClick={() => navigate("/planning/mrp-runs")}
            style={{
              padding: "0.6rem 1rem",
              borderRadius: "12px",
              background: "rgba(255, 255, 255, 0.15)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.3)",
              fontWeight: 700,
              fontSize: "0.85rem",
              cursor: "pointer",
            }}
          >
            بازگشت
          </button>
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>
        <table className="w-full text-sm text-left rtl:text-right border-collapse">
          <thead>
            <tr>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">ماده</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">مقدار موردنیاز</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">تاریخ موردنیاز</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">نوع تقاضا</th>
              <th className="px-3 py-2.5 text-muted font-medium border-b border-white/10">پیشنهاد تأمین</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ requirement, depth }) => (
              <tr key={requirement.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                <td className="px-3 py-2.5" style={{ paddingRight: `${(depth * 1.5) + 0.75}rem` }}>
                  {depth > 0 && <span style={{ color: "var(--color-text-muted)", marginLeft: "0.25rem" }}>└─</span>}
                  {getMaterialName(requirement.material_family)}
                </td>
                <td className="px-3 py-2.5">{requirement.required_qty}</td>
                <td className="px-3 py-2.5">{formatDate(requirement.required_date)}</td>
                <td className="px-3 py-2.5">
                  {requirement.demand_type === "independent"
                    ? "مستقل (از MPS)"
                    : "وابسته (انفجار BOM)"}
                </td>
                <td className="px-3 py-2.5">
                  <SourcingBadge value={requirement.sourcing_suggestion} />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-muted">
                  هیچ نیازی در این اجرا ثبت نشده است.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
